import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import type { WCardSuiteSummaryView } from '@zola/svc-web-api-ts-client';
import type { CardImageSize } from 'components/common/ui/CardImage/types';
import { getCardSuitePath } from '@zola/zola-ui/src/paper/cards/util/getCardUrl';
import type { CardType, LeadCardType } from '@zola/zola-ui/src/paper/cards/constants/cardTypes';
import { priceFormatter } from 'components/product/PricingUtils';
import { ProductColorPickerProps } from 'cards/components/common/ProductColorPicker/ProductColorPicker';
import { FavoriteSuiteTogglePlaceholder } from 'favorites/components/FavoriteSuiteToggle/FavoriteSuiteTogglePlaceholder';

import { SuiteName } from './SuiteName';
import { getCardDefaultColor, getCardThumbnailByColor } from './utils';
import {
  Tile,
  StyledCardImage,
  FavoriteToggleWrapper,
  StyledProductColorPicker,
  SuitePrice,
  SuiteColors,
  SuiteImage,
} from './SuiteTile.style';

const IMAGE_SRC_SIZES: CardImageSize[] = [
  { width: 200, media: '(max-width: 496px)' },
  { width: 280, media: '(max-width: 768px)' },
  { width: 232, media: '(max-width: 991px)' },
];
const IMAGE_SRC_DEFAULT_WIDTH = 300;

const FavoriteSuiteToggle = dynamic(() => import('favorites/components/FavoriteSuiteToggle'), {
  ssr: false,
  loading: () => <FavoriteSuiteTogglePlaceholder height={20} />,
});

interface SuiteTileProps {
  suite: WCardSuiteSummaryView;
  defaultColor?: string;
  hideTags?: boolean;
  hidePrice?: boolean;
  hideColorPickerInMobile?: boolean;
  colorPickerOptions?: Partial<ProductColorPickerProps>;
  className?: string;
}

export const SuiteTile: React.FC<SuiteTileProps> = ({
  suite,
  defaultColor,
  hideTags,
  hidePrice,
  hideColorPickerInMobile,
  colorPickerOptions,
  className,
}) => {
  const { lead_card: card, uuid: suiteUUID = '' } = suite;

  const [activeColor, setActiveColor] = useState(defaultColor || getCardDefaultColor(card));

  if (!card) return null;

  const thumbnail = getCardThumbnailByColor(card, activeColor);
  const [, cardName] = card.name?.split(' - ') || [];

  const pathname = getCardSuitePath(card.type as LeadCardType, suiteUUID);
  const price = card.min_price_cents ? card.min_price_cents / 100 : 0;

  return (
    <Tile className={className}>
      <FavoriteToggleWrapper>
        <FavoriteSuiteToggle height={20} suiteType={card.type as CardType} suiteUUID={suiteUUID} />
      </FavoriteToggleWrapper>
      <SuiteImage>
        <LinkV2 href={pathname} aria-label={card.name} noUnderline>
          <StyledCardImage
            src={thumbnail}
            alt={cardName}
            defaultWidth={IMAGE_SRC_DEFAULT_WIDTH}
            sourceSizes={IMAGE_SRC_SIZES}
          />
        </LinkV2>
      </SuiteImage>
      <SuiteName suite={suite} hideTags={hideTags} />
      <StyledProductColorPicker
        colors={card.colors}
        activeColor={activeColor}
        onSelect={colorObject => setActiveColor(colorObject.value || '')}
        swatchWidth={24}
        hideColorPickerInMobile={hideColorPickerInMobile}
        {...colorPickerOptions}
      />
      {hideColorPickerInMobile && <SuiteColors>{card.colors?.length} colors</SuiteColors>}
      {!hidePrice && <SuitePrice>From {priceFormatter(price)} each</SuitePrice>}
    </Tile>
  );
};
